import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LanguagePicker } from '../components/LanguagePicker';
import {
  Card,
  PulseCircle,
  Text,
  type CheckStatus,
} from '../components/ds';
import { useLanguage, type Language, type Translate, type TranslationKey } from '../i18n';

interface EventLogEntry {
  timestamp: string;
  event_description: string;
}

interface StatusData {
  check_name?: string;
  current_status: CheckStatus;
  /**
   * The language the check was created in, and the one every email about it is
   * written in. Absent until the backend chunk deploys — and absent is not an
   * error, it just means the resolution chain falls through to the browser.
   */
  language?: string;
  last_successful_ping?: string;
  next_expected_ping?: string;
  time_until_next_event_seconds?: number;
  event_log: EventLogEntry[];
  fetched_at_timestamp?: string;
}

const POLL_MS = 30_000;

type Tone = 'ok' | 'warning' | 'alert' | 'neutral' | 'idle';

interface ToneConfig {
  tone: Tone;
  label: string;
  headline: string;
  description: string;
  animate: boolean;
}

const toneFor = (status: CheckStatus, t: Translate): ToneConfig => {
  switch (status) {
    case 'ACTIVE_UP':
      return {
        tone: 'ok',
        label: t('monitor.status.active.label'),
        headline: t('monitor.status.active.headline'),
        description: t('monitor.status.active.description'),
        animate: true,
      };
    case 'GRACE_PERIOD_LATE':
      return {
        tone: 'warning',
        label: t('monitor.status.grace.label'),
        headline: t('monitor.status.grace.headline'),
        description: t('monitor.status.grace.description'),
        animate: false,
      };
    case 'ALERTED_DOWN':
      return {
        tone: 'alert',
        label: t('monitor.status.alerted.label'),
        headline: t('monitor.status.alerted.headline'),
        description: t('monitor.status.alerted.description'),
        animate: false,
      };
    case 'RESOLVED':
      return {
        tone: 'ok',
        label: t('monitor.status.resolved.label'),
        headline: t('monitor.status.resolved.headline'),
        description: t('monitor.status.resolved.description'),
        animate: false,
      };
    case 'COMPLETED':
      return {
        tone: 'idle',
        label: t('monitor.status.completed.label'),
        headline: t('monitor.status.completed.headline'),
        description: t('monitor.status.completed.description'),
        animate: false,
      };
    case 'CANCELED':
      // Tone `idle` is also what suppresses the countdown below — a deadline
      // for an event that will never come is what strands a receiver here.
      return {
        tone: 'idle',
        label: 'Canceled',
        headline: 'This check-in was cancelled',
        description:
          'The person who set it up ended it. You will not be notified about it again.',
        animate: false,
      };
    case 'PENDING_START':
    default:
      return {
        tone: 'neutral',
        label: t('monitor.status.pending.label'),
        headline: t('monitor.status.pending.headline'),
        description: t('monitor.status.pending.description'),
        animate: false,
      };
  }
};

/**
 * What went wrong, not what to say about it. The page used to keep the English
 * sentence in state and sniff its prefix to decide which error this was, which
 * stops being true the moment the sentence is translated.
 */
type MonitorErrorKind = 'missing-link' | 'invalid-link' | 'load-failed' | 'unreachable';

const errorCopy: Record<
  MonitorErrorKind,
  { titleKey: TranslationKey; bodyKey: TranslationKey; tone: Tone; retryable: boolean }
> = {
  'missing-link': {
    titleKey: 'monitor.error.title.unavailable',
    bodyKey: 'monitor.error.body.missingLink',
    tone: 'warning',
    retryable: true,
  },
  'invalid-link': {
    titleKey: 'monitor.error.title.invalidLink',
    bodyKey: 'monitor.error.body.invalidLink',
    tone: 'idle',
    retryable: false,
  },
  'load-failed': {
    titleKey: 'monitor.error.title.unavailable',
    bodyKey: 'monitor.error.body.loadFailed',
    tone: 'warning',
    retryable: true,
  },
  unreachable: {
    titleKey: 'monitor.error.title.unavailable',
    bodyKey: 'monitor.error.body.unreachable',
    tone: 'warning',
    retryable: true,
  },
};

const MonitorPage: React.FC = () => {
  const { monitor_link_id } = useParams<{ monitor_link_id: string }>();
  const { t, setCheckLanguage } = useLanguage();
  const [data, setData] = useState<StatusData | null>(null);
  const [error, setError] = useState<MonitorErrorKind | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = useCallback(
    async (manual = false) => {
      if (!monitor_link_id) {
        setError('missing-link');
        setLoading(false);
        return;
      }
      if (manual) setRefreshing(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const url = `${apiUrl}/api/v1/status/${monitor_link_id}`;
      try {
        const res = await fetch(url);
        if (!res.ok) {
          setError(res.status === 404 ? 'invalid-link' : 'load-failed');
        } else {
          const fresh = (await res.json()) as StatusData;
          setCheckLanguage(fresh.language);
          setData({ ...fresh, fetched_at_timestamp: new Date().toISOString() });
        }
      } catch {
        setError('unreachable');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [monitor_link_id, setCheckLanguage],
  );

  useEffect(() => {
    void fetchStatus();
    const id = setInterval(() => void fetchStatus(false), POLL_MS);
    return () => clearInterval(id);
  }, [fetchStatus]);

  if (loading && !data) {
    return <LoadingState />;
  }

  if (error && !data) {
    return (
      <ErrorState
        kind={error}
        onRetry={() => void fetchStatus(true)}
        retrying={refreshing}
      />
    );
  }

  if (!data) return null;

  const cfg = toneFor(data.current_status, t);

  return (
    <div className="min-h-screen bg-surface-bg flex flex-col">
      <Header
        rightSlot={
          <button
            onClick={() => void fetchStatus(true)}
            disabled={refreshing}
            aria-label={t('monitor.refresh.label')}
            className="text-text-secondary hover:text-text-primary p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 transition-base"
          >
            <RefreshIcon spinning={refreshing} />
          </button>
        }
      />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 flex flex-col gap-6">
        {error && data ? (
          <div className="rounded-md p-3 bg-status-alert-soft border border-status-alert/20">
            <Text variant="body" className="text-status-alert">
              {t(errorCopy[error].bodyKey)} {t('monitor.error.stale')}
            </Text>
          </div>
        ) : null}

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-3">
            <StatusHero data={data} cfg={cfg} />
          </div>
          <div className="lg:col-span-2 flex flex-col gap-6 lg:sticky lg:top-24">
            <DetailsCard data={data} />
            {data.event_log && data.event_log.length > 0 ? (
              <ActivityCard events={data.event_log} />
            ) : null}
          </div>
        </div>

        <AboutBlurb />
      </main>

      <Footer fetchedAt={data.fetched_at_timestamp} refreshing={refreshing} />
    </div>
  );
};

/* --------------------------- sections --------------------------- */

const StatusHero: React.FC<{ data: StatusData; cfg: ToneConfig }> = ({ data, cfg }) => {
  const { t } = useLanguage();
  const toneText: Record<Tone, string> = {
    ok: 'text-status-ok',
    warning: 'text-status-late',
    alert: 'text-status-alert',
    neutral: 'text-brand-primary',
    idle: 'text-text-muted',
  };
  const toneBg: Record<Tone, string> = {
    ok: 'bg-status-ok-soft',
    warning: 'bg-status-late-soft',
    alert: 'bg-status-alert-soft',
    neutral: 'bg-brand-primary-soft',
    idle: 'bg-gray-100',
  };

  return (
    <Card elevated className="p-6 sm:p-10 lg:p-12 flex flex-col items-center text-center gap-5 animate-fade-up">
      <PulseCircle tone={cfg.tone} size="lg" animate={cfg.animate} />
      <div className="flex flex-col items-center gap-2">
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${toneBg[cfg.tone]}`}
        >
          <span className={`w-2 h-2 rounded-full ${toneText[cfg.tone].replace('text-', 'bg-')}`} />
          <Text variant="label" className={toneText[cfg.tone]}>
            {cfg.label}
          </Text>
        </span>
        <Text as="h1" variant="display" className="mt-2">
          {/* The creator typed this name. It is echoed as typed, never translated. */}
          {data.check_name ?? t('monitor.defaultCheckName')}
        </Text>
        <Text variant="body-lg" color="primary" className="font-medium">
          {cfg.headline}
        </Text>
        <Text variant="body" color="muted" className="max-w-md">
          {cfg.description}
        </Text>
      </div>

      {data.next_expected_ping && cfg.tone !== 'idle' ? (
        <NextPingCountdown targetIso={data.next_expected_ping} />
      ) : null}
    </Card>
  );
};

const NextPingCountdown: React.FC<{ targetIso: string }> = ({ targetIso }) => {
  const { t } = useLanguage();
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const formatted = useMemo(() => formatRelative(targetIso, t), [targetIso, t]);
  const overdue = new Date(targetIso).getTime() < Date.now();

  return (
    <div className="mt-4 rounded-lg bg-surface-bg px-6 py-4 flex flex-col items-center gap-1">
      <Text variant="caption" color="muted" className="uppercase tracking-widest">
        {overdue ? t('monitor.countdown.overdue') : t('monitor.countdown.next')}
      </Text>
      <Text variant="title" tabular className={overdue ? 'text-status-late' : ''}>
        {formatted}
      </Text>
    </div>
  );
};

const DetailsCard: React.FC<{ data: StatusData }> = ({ data }) => {
  const { t, language } = useLanguage();
  return (
    <Card className="p-6 animate-fade-up" style={{ animationDelay: '60ms' }}>
      <Text variant="label" color="muted" className="uppercase tracking-widest mb-4 block">
        {t('monitor.details.title')}
      </Text>
      <div className="grid sm:grid-cols-2 gap-5">
        <DetailItem
          label={t('monitor.details.lastOk')}
          value={
            data.last_successful_ping ? formatDateTime(data.last_successful_ping, language) : '—'
          }
          icon={
            <span className="w-9 h-9 rounded-full bg-status-ok-soft inline-flex items-center justify-center text-status-ok">
              <CheckIcon />
            </span>
          }
        />
        <DetailItem
          label={t('monitor.details.nextPing')}
          value={data.next_expected_ping ? formatDateTime(data.next_expected_ping, language) : '—'}
          icon={
            <span className="w-9 h-9 rounded-full bg-brand-secondary-soft inline-flex items-center justify-center text-brand-secondary">
              <ClockIcon />
            </span>
          }
        />
      </div>
    </Card>
  );
};

const DetailItem: React.FC<{ label: string; value: string; icon: React.ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <div className="flex items-start gap-3">
    {icon}
    <div className="flex flex-col">
      <Text variant="caption" color="muted">
        {label}
      </Text>
      <Text variant="body" tabular className="mt-1">
        {value}
      </Text>
    </div>
  </div>
);

const ActivityCard: React.FC<{ events: EventLogEntry[] }> = ({ events }) => {
  const { t, language } = useLanguage();
  return (
    <Card className="p-6 animate-fade-up" style={{ animationDelay: '120ms' }}>
      <Text variant="label" color="muted" className="uppercase tracking-widest mb-4 block">
        {t('monitor.activity.title')}
      </Text>
      <ul className="flex flex-col">
        {events.map((e, i) => {
          // Decoration only, and only as good as the words it is guessing at.
          // The backend sends these descriptions already written in the check's
          // language — the wire shape is a display string, not a key — so once
          // it speaks German these stop matching and every entry falls back to
          // the neutral dot. Fixing that properly needs an event kind on the
          // wire, which spans the API and both clients.
          const isPing = /ping|received|ok/i.test(e.event_description);
          const isAlert = /alert|missed|fail/i.test(e.event_description);
          const isResolved = /resolved/i.test(e.event_description);
          return (
            <li
              key={`${e.timestamp}-${i}`}
              className={`flex items-start gap-3 py-3 ${
                i < events.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <span
                className={`w-9 h-9 rounded-full inline-flex items-center justify-center flex-shrink-0 ${
                  isAlert
                    ? 'bg-status-alert-soft text-status-alert'
                    : isResolved
                    ? 'bg-status-ok-soft text-status-ok'
                    : isPing
                    ? 'bg-status-ok-soft text-status-ok'
                    : 'bg-brand-secondary-soft text-brand-secondary'
                }`}
              >
                {isAlert ? <AlertIcon /> : isPing || isResolved ? <CheckIcon /> : <DotIcon />}
              </span>
              <div className="flex-1 min-w-0">
                <Text variant="body">{e.event_description}</Text>
                <Text variant="caption" color="muted" tabular className="mt-1">
                  {formatDateTime(e.timestamp, language)}
                </Text>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

const AboutBlurb: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="text-center px-4 pt-2">
      <Text variant="caption" color="muted">
        {t('monitor.about')}
      </Text>
    </div>
  );
};

/* ------------------------- empty / error / loading ------------------------- */

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-surface-bg flex flex-col">
    <Header />
    <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 py-12 flex flex-col gap-6">
      <Card elevated className="p-8 sm:p-10 flex flex-col items-center text-center gap-5">
        <div className="w-40 h-40 rounded-full bg-gray-100 animate-shimmer" />
        <div className="w-40 h-6 rounded-md bg-gray-100 animate-shimmer" />
        <div className="w-64 h-4 rounded-md bg-gray-100 animate-shimmer" />
      </Card>
      <Card className="p-6">
        <div className="w-24 h-3 rounded bg-gray-100 mb-4 animate-shimmer" />
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="h-12 rounded bg-gray-100 animate-shimmer" />
          <div className="h-12 rounded bg-gray-100 animate-shimmer" />
        </div>
      </Card>
    </main>
    <Footer />
  </div>
);

const ErrorState: React.FC<{
  kind: MonitorErrorKind;
  onRetry: () => void;
  retrying: boolean;
}> = ({ kind, onRetry, retrying }) => {
  const { t } = useLanguage();
  const copy = errorCopy[kind];
  return (
    <div className="min-h-screen bg-surface-bg flex flex-col">
      <Header />
      <main className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5 max-w-md mx-auto">
        <PulseCircle tone={copy.tone} size="md" animate={false} />
        <Text as="h1" variant="title">
          {t(copy.titleKey)}
        </Text>
        <Text variant="body" color="muted">
          {t(copy.bodyKey)}
        </Text>
        {copy.retryable ? (
          <button
            onClick={onRetry}
            disabled={retrying}
            className="mt-2 rounded-full bg-brand-secondary text-white py-3 px-6 font-semibold hover:opacity-90 disabled:opacity-50 transition-base shadow-elev-2"
          >
            {retrying ? t('monitor.error.retrying') : t('monitor.error.retry')}
          </button>
        ) : null}
      </main>
      <Footer />
    </div>
  );
};

/* ----------------------------- chrome ----------------------------- */

const Header: React.FC<{ rightSlot?: React.ReactNode }> = ({ rightSlot }) => (
  <header className="sticky top-0 z-20 bg-surface-card/80 backdrop-blur border-b border-gray-200">
    <div className="max-w-2xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
      <a href="/" className="inline-flex items-center gap-2">
        <span className="font-display text-heading text-brand-primary tracking-tight">iam.ok</span>
      </a>
      <div className="flex items-center gap-2">
        {/* A receiver who does not read the creator's language is one tap from
            one they do. */}
        <LanguagePicker />
        {rightSlot}
      </div>
    </div>
  </header>
);

const Footer: React.FC<{ fetchedAt?: string; refreshing?: boolean }> = ({
  fetchedAt,
  refreshing,
}) => {
  const { t, language } = useLanguage();
  return (
    <footer className="py-6 px-4 sm:px-6 border-t border-gray-200 bg-surface-card">
      <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
        {fetchedAt ? (
          <Text variant="caption" color="muted" tabular>
            {t('monitor.footer.updated', { time: formatDateTime(fetchedAt, language) })}
            {refreshing ? ` · ${t('monitor.footer.refreshing')}` : ''}
          </Text>
        ) : (
          <span />
        )}
        <Text variant="caption" color="muted">
          {t('common.footer.copyright', { year: new Date().getFullYear() })}
        </Text>
      </div>
    </footer>
  );
};

/* ----------------------------- helpers ----------------------------- */

function formatDateTime(iso: string, language: Language): string {
  return new Date(iso).toLocaleString(language, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelative(iso: string, t: Translate): string {
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const total = Math.floor(abs / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (days > 0) return t('monitor.duration.dayHour', { days, hours });
  if (hours > 0) return t('monitor.duration.hourMinute', { hours, minutes: pad(minutes) });
  return t('monitor.duration.minuteSecond', { minutes, seconds: pad(seconds) });
}

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

/* ----------------------------- icons ----------------------------- */

const RefreshIcon: React.FC<{ spinning?: boolean }> = ({ spinning }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={spinning ? { animation: 'spin 1s linear infinite' } : undefined}
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const AlertIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 9v4M12 17h.01" />
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
  </svg>
);

const DotIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="6" />
  </svg>
);

export default MonitorPage;
